// src/frontend/context/NotificationContext.jsx
import React, {createContext, useContext, useEffect, useState} from 'react';
import {socket} from '../lib/socket';
import {SOCKET_EVENTS} from '../constants/events';

const NotificationContext = createContext();

export function NotificationProvider({children}) {
    const [notifications, setNotifications] = useState([]);

    // Lade aus LocalStorage beim Mount
    useEffect(() => {
        const saved = localStorage.getItem('notifications');
        if (saved) {
            setNotifications(JSON.parse(saved));
        }
    }, []);

    // Speichere bei jeder Änderung
    useEffect(() => {
        localStorage.setItem('notifications', JSON.stringify(notifications));
    }, [notifications]);

    // Socket-Listener für globale + raum-spezifische Events
    useEffect(() => {
        // Globale System-Nachricht
        socket.on(SOCKET_EVENTS.GLOBAL_NOTIFICATION, (message) => {
            addNotification({
                id: Date.now(),
                title: 'System',
                message,
                type: 'info',
                read: false,
                timestamp: new Date().toLocaleString(),
            });
        });

        // Neue Datei hochgeladen (einzelne Datei)
        socket.on(SOCKET_EVENTS.FILE_UPLOADED, (newFile) => {
            const fileInfo = Array.isArray(newFile) ? newFile[0] : newFile;
            if (fileInfo?.name) {
                addNotification({
                    id: Date.now(),
                    title: 'Neue Datei',
                    message: `Datei ${fileInfo.name} hochgeladen von ${fileInfo.uploadedBy || 'anonymous'}`,
                    type: 'file-uploaded',
                    read: false,
                    timestamp: new Date().toLocaleString(),
                });
            }
        });

        // User-Liste Updates → Diff berechnen für join/leave
        let prevUsers = [];
        socket.on(SOCKET_EVENTS.UPDATE_USERS, (currentUsers) => {
            if (prevUsers.length === 0) {
                // Erste Liste beim Join → nichts pushen
                prevUsers = [...currentUsers];
                return;
            }

            const joined = currentUsers.filter(u => !prevUsers.includes(u));
            const left = prevUsers.filter(u => !currentUsers.includes(u));

            joined.forEach(username => {
                addNotification({
                    id: Date.now() + Math.random(),
                    title: 'User beigetreten',
                    message: `${username} ist dem Raum beigetreten`,
                    type: 'user-joined',
                    read: false,
                    timestamp: new Date().toLocaleString(),
                });
            });

            left.forEach(username => {
                addNotification({
                    id: Date.now() + Math.random(),
                    title: 'User verlassen',
                    message: `${username} hat den Raum verlassen`,
                    type: 'user-left',
                    read: false,
                    timestamp: new Date().toLocaleString(),
                });
            });

            prevUsers = [...currentUsers];
        });

        return () => {
            socket.off(SOCKET_EVENTS.GLOBAL_NOTIFICATION);
            socket.off(SOCKET_EVENTS.FILE_UPLOADED);
            socket.off(SOCKET_EVENTS.UPDATE_USERS);
        };
    }, []);

    const addNotification = (notif) => {
        setNotifications((prev) => [...prev, notif]);
    };

    const markAsRead = (id) => {
        setNotifications((prev) => prev.map(n => n.id === id ? {...n, read: true} : n));
    };

    const removeNotification = (id) => {
        setNotifications((prev) => prev.filter(n => n.id !== id));
    };

    const clearAll = () => {
        setNotifications([]);
    };

    const unreadCount = notifications.filter(n => !n.read).length;

    return (
        <NotificationContext.Provider value={{
            notifications,
            addNotification,
            markAsRead,
            removeNotification,
            clearAll,
            unreadCount,
        }}>
            {children}
        </NotificationContext.Provider>
    );
}

export const useNotifications = () => useContext(NotificationContext);